import { uploadDocAppendix } from "../../documents/services/documents.service";
import { userAtom } from "../../shared/atoms";
import { toastError } from "../../shared/components/ToastContainer";
import { Certificate, CertificateDocumentPage } from "../models/certificates.interfaces";
import { useAtomValue } from "jotai";
import { useRef } from "react";

const ACCEPTED_FORMATS = {
  PDF: "application/pdf",
};

export interface Props {
  parentDoc: Certificate;
  inner: React.ReactNode | undefined;
  searchDocs: () => void;
}

const UploadButton: React.FC<Props> = ({ inner, parentDoc, searchDocs }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const user = useAtomValue(userAtom);

  const cacheFileForUpload = async (file: File) => {
    if (file.type !== ACCEPTED_FORMATS.PDF) return;

    const newDoc: CertificateDocumentPage = {
      cachedFile: file,
      fileName: file.name,
      vesselCode: parentDoc.vesselCode,
      vesselName: parentDoc.vesselName,
      imoNumber: parentDoc.imoNumber,
      vesselMasterEmail: parentDoc.vesselMasterEmail,
      docName: "",
      docType: "Appendix",
      source: "Manual upload",
      expiresOn: null,
      nextSurveyDate: null,
      appendixFor: parentDoc.s3Path ?? "",
    };

    try {
      uploadDocAppendix(newDoc, user);
    } catch (err) {
      console.error("Appendinx failed to upload", err);
      const error = err instanceof Error ? err : new Error(String(err));
      console.log(error); // Render popup
      toastError("Appendinx failed to upload");
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filePromises = Array.from(e.target.files).map((file) => cacheFileForUpload(file));
      await Promise.all(filePromises);

      searchDocs();
    }
    // Removing this causes a bug, the input needs to be cleared for handleFileChange to be called with the same file (we handle duplicates)
    e.target.value = "";
  };

  return (
    <div onClick={handleClick}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileChange}
        accept={Object.values(ACCEPTED_FORMATS).toString()}
        data-testid="file-uploader"
        style={{ display: "none" }}
        name="file"
      />
      {inner}
    </div>
  );
};

export default UploadButton;
